import { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  fetchBillingSubscription,
  fetchPerSeatInfo as fetchPerSeatInfoRequest,
  fetchProrationPreview,
  createBillingPortalSession,
  createPerSeatCheckoutSession,
  createCheckoutSession,
  updateBillingInterval,
} from "../services/billing.service";
import { acceptTerms, acceptTOS } from "../services/auth.service";
import { DataContext } from "../contexts/DataProvider";
import TermsOfServiceModal from "../components/TermsOfServiceModal";
import CircleIcon from "@mui/icons-material/Circle";
import { Info } from "lucide-react";

interface PaymentsTabProps {
  orgId: string;
}

interface ProrationPreview {
  newInterval: string;
  effectiveDate: string;
  currency: string;
  nextRenewalDate: string | null;
  nextRenewalAmount: string | null;
}

const CURRENT_TOS_VERSION = "1.0";
const TOS_TYPE = "TERMS_OF_SERVICE";

const PaymentsTab: React.FC<PaymentsTabProps> = ({ orgId }) => {
  const per_seat_price_id = "price_1TF4zyBM7tW91bxLrHD2gaMu";
  const price_yearly_id = "price_1TF5KbBM7tW91bxLdRP3uZTn";

  // ─── Core billing state ─────────────────────────────────────────────────────
  const [loading, setLoading] = useState<boolean>(true);
  const [subscribing, setSubscribing] = useState<boolean>(false);
  const [subscribingId, setSubscribingId] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<Record<
    string,
    any
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [perSeatLoading, setPerSeatLoading] = useState<boolean>(true);
  const [perSeatInfo, setPerSeatInfo] = useState<{
    monthly?: {
      priceId: string;
      pricePerSeat: number;
      activeUserCount: number;
      totalAmount: number;
      billingInterval: string;
      message?: string;
      note?: string;
    };
    yearly?: {
      priceId: string;
      pricePerSeat: number;
      activeUserCount: number;
      totalAmount: number;
      billingInterval: string;
      message?: string;
      note?: string;
      pricePerSeatMonthly?: string;
    };
  } | null>(null);

  // ─── Plan switching state ────────────────────────────────────────────────────
  // `switching` covers both the preview-fetch phase and the confirm phase
  const [switching, setSwitching] = useState<boolean>(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  const [prorationPreview, setProrationPreview] =
    useState<ProrationPreview | null>(null);

  // ─── ToS state ──────────────────────────────────────────────────────────────
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [tosLoading, setTosLoading] = useState(false);
  const [tosError, setTosError] = useState<string | null>(null);
  const pendingSubscribeRef = useRef<null | (() => void)>(null);

  const { login, setLogin } = useContext(DataContext) as {
    login: {
      data?: {
        email?: string;
        name?: string;
        organisation?: any;
        hasAcceptedToS?: boolean;
      };
    };
    setLogin: any;
  };

  // ─── Derived values ──────────────────────────────────────────────────────────
  const monthlyPrice = perSeatInfo?.monthly?.pricePerSeat || 0;
  const yearlyPrice = perSeatInfo?.yearly?.pricePerSeat || 0;
  const users = perSeatInfo?.yearly?.activeUserCount || 1;
  const totalMonthly = monthlyPrice * users * 12;
  const totalYearly = yearlyPrice * users;
  const savings = totalMonthly - totalYearly;
  const savingsPerUser = monthlyPrice * 12 - yearlyPrice;
  const percentSaved =
    totalMonthly > 0 ? Math.round((savings / totalMonthly) * 100) : 0;

  const features = [
    "Full Prospo Platform Access",
    "Built-in CRM",
    "AI Meeting Transcription",
    "AI File Note Generation",
    "AI Fact Find Automation",
    "AI Client Graph Visualisation",
    "AI PowerPoint Generation",
    "AI Email Drafting",
    "10+ Third-Party Integrations",
    "Unlimited Clients & Meetings",
    `Save ${percentSaved}% with annual billing`,
  ];

  const billingRules = [
    "Charged per active user seat",
    "Auto-adjusts as your team grows",
    "Prorated automatically",
    "Annual billing at discounted rate",
  ];

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const hasAcceptedLatestTOS = () => {
    if (login?.data?.hasAcceptedToS) return true;
    const org = login?.data?.organisation;
    if (!org || !Array.isArray(org.legalAgreements)) return false;
    return org.legalAgreements.some(
      (a: any) => a.type === TOS_TYPE && a.version === CURRENT_TOS_VERSION,
    );
  };

  const interceptSubscribe = (action: () => void) => {
    if (hasAcceptedLatestTOS()) {
      action();
    } else {
      pendingSubscribeRef.current = action;
      setTosModalOpen(true);
    }
  };

  // ─── Data fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (orgId && orgId !== "All Organisation") {
      fetchBillingStatus();
      fetchPerSeatInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const fetchBillingStatus = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchBillingSubscription(orgId);
      setSubscriptionData(res);
    } catch (err: any) {
      setError(err?.message || "Failed to load subscription data");
      setSubscriptionData({ status: "inactive", hasSubscription: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchPerSeatInfo = async (): Promise<void> => {
    try {
      setPerSeatLoading(true);
      const res: any = await fetchPerSeatInfoRequest(orgId);
      setPerSeatInfo(res);
    } catch (err: any) {
      console.error("❌ Error fetching per-seat info:", err);
      setPerSeatInfo(null);
    } finally {
      setPerSeatLoading(false);
    }
  };

  // ─── Action handlers ──────────────────────────────────────────────────────────
  const handleManageBilling = async (): Promise<void> => {
    setSubscribing(true);
    try {
      const response = await createBillingPortalSession(orgId);
      const portalRes = response as { url?: string };
      if (portalRes?.url) {
        window.location.href = portalRes.url;
      } else {
        setError("Failed to open billing portal");
        setSubscribing(false);
      }
    } catch (err) {
      console.error("Error opening billing portal:", err);
      setError("Failed to open billing portal");
      setSubscribing(false);
    }
  };

  const handleSubscribePerSeat = async (): Promise<void> => {
    const priceId = perSeatInfo?.monthly?.priceId || per_seat_price_id;
    interceptSubscribe(async () => {
      setSubscribingId(priceId);
      try {
        const response = await createPerSeatCheckoutSession({
          email: login?.data?.email,
          name: login?.data?.name,
          priceId,
          orgId,
        });
        const checkoutRes = response as { url?: string };
        if (checkoutRes?.url) {
          window.location.href = checkoutRes.url;
        } else {
          setError("Failed to start per-seat subscription");
          setSubscribingId(null);
        }
      } catch (err) {
        console.error("Error subscribing per-seat:", err);
        setError("Failed to start per-seat subscription");
        setSubscribing(false);
      }
    });
  };

  const handleSubscribe = async (priceId: string): Promise<void> => {
    interceptSubscribe(async () => {
      setSubscribingId(priceId);
      try {
        const response = await createCheckoutSession({
          email: login?.data?.email,
          name: login?.data?.name,
          quantity: 1,
          priceId,
          orgId,
        });
        const checkoutRes = response as { url?: string };
        if (checkoutRes?.url) {
          window.location.href = checkoutRes.url;
        } else {
          setError("Failed to start subscription");
          setSubscribing(false);
        }
      } catch (err) {
        console.error("Error subscribing:", err);
        setError("Failed to start subscription");
        setSubscribingId(null);
      }
    });
  };

  // Step 1: fetch proration preview — `switching` + `pendingPriceId` are set
  // immediately so the clicked button shows a spinner while loading. After the
  // fetch, `switching` is cleared so confirm/cancel buttons become interactive
  // while `pendingPriceId` stays set to keep the other switch buttons disabled.
  const handleSwitchPlan = async (newPriceId: string): Promise<void> => {
    setSwitching(true);
    setPendingPriceId(newPriceId); // set before fetch so the button shows a spinner
    setSwitchError(null);
    setProrationPreview(null);
    try {
      const preview: any = await fetchProrationPreview(orgId, newPriceId);
      setProrationPreview(preview);
      setSwitching(false); // re-enable confirm/cancel while user reads the alert
    } catch (err: any) {
      setSwitchError(err?.message || "Failed to preview plan switch.");
      setPendingPriceId(null); // clear so switch buttons re-enable after an error
      setSwitching(false);
    }
  };

  // Step 2: user confirmed — execute the switch
  const confirmSwitchPlan = async (): Promise<void> => {
    if (!pendingPriceId) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      await updateBillingInterval(orgId, pendingPriceId);
      setProrationPreview(null);
      setPendingPriceId(null);
      await fetchBillingStatus(); // billingPeriod now reflects new interval
    } catch (err: any) {
      setSwitchError(err?.message || "Failed to switch plan.");
    } finally {
      setSwitching(false);
    }
  };

  const cancelSwitchPlan = () => {
    setProrationPreview(null);
    setPendingPriceId(null);
    setSwitchError(null);
  };

  // ─── ToS accept ───────────────────────────────────────────────────────────────
  const handleTosAccept = async (documentHash: string) => {
    setTosLoading(true);
    setTosError(null);
    try {
      await acceptTerms({
        version: CURRENT_TOS_VERSION,
        type: TOS_TYPE,
        documentHash,
        method: "checkbox",
      });
      try {
        const acceptRes: any = await acceptTOS();
        if (acceptRes?.token) localStorage.setItem("token", acceptRes.token);
        setLogin((prev: any) => ({
          ...prev,
          data: { ...prev?.data, hasAcceptedToS: true },
        }));
      } catch (e) {
        console.error("[ToS] failed to refresh token after accept:", e);
      }
      setTosModalOpen(false);
      if (pendingSubscribeRef.current) {
        const fn = pendingSubscribeRef.current;
        pendingSubscribeRef.current = null;
        fn();
      }
    } catch (err) {
      setTosError("Unable to record agreement. Please try again.");
    } finally {
      setTosLoading(false);
    }
  };

  // ─── Render: loading ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Derived state (post-load) ────────────────────────────────────────────────
  const isActive =
    subscriptionData?.status === "active" ||
    subscriptionData?.status === "trialing";

  // null means we don't know yet (e.g. exempt org or data not loaded)
  const currentInterval: "monthly" | "yearly" | null =
    subscriptionData?.billingPeriod === "Yearly"
      ? "yearly"
      : subscriptionData?.billingPeriod === "Monthly"
        ? "monthly"
        : null;

  // ─── Render: slim status banner ───────────────────────────────────────────────
  const renderStatusBanner = () => (
    <Box
      sx={{
        width: "100%",
        mb: 3,
        p: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          flexWrap="wrap"
        >
          <Chip
            label={
              subscriptionData?.cancelAtPeriodEnd ? "Ending Soon" : "Active"
            }
            color={subscriptionData?.cancelAtPeriodEnd ? "warning" : "success"}
            size="small"
            sx={{ fontWeight: 700 }}
          />
          <Typography variant="body2" fontWeight={600}>
            {subscriptionData?.planName || "Prospo Subscription"}
          </Typography>
          {!subscriptionData?.cancelAtPeriodEnd &&
            subscriptionData?.nextInvoiceDate &&
            subscriptionData.nextInvoiceDate !== "N/A" && (
              <Typography variant="body2" color="text.secondary">
                · Next invoice: {subscriptionData.nextInvoiceDate} · Est.{" "}
                {subscriptionData.estimatedAmount}
              </Typography>
            )}
        </Stack>
        <Button
          variant="text"
          size="small"
          onClick={handleManageBilling}
          disabled={subscribing}
          sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
          endIcon={
            subscribing ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          Manage Billing & Invoices →
        </Button>
      </Stack>

      {subscriptionData?.cancelAtPeriodEnd && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ mt: 1, display: "block" }}
        >
          Access ends{" "}
          {subscriptionData.currentPeriodEnd
            ? new Intl.DateTimeFormat("en-AU", {
                day: "numeric",
                month: "short",
                year: "numeric",
              }).format(new Date(subscriptionData.currentPeriodEnd))
            : subscriptionData.nextInvoiceDate}
          . Resume your subscription to continue switching plans.
        </Typography>
      )}

      {subscriptionData?.pendingSwitchInterval && !subscriptionData?.cancelAtPeriodEnd && (
        <Typography
          variant="caption"
          color="info.main"
          sx={{ mt: 1, display: "block" }}
        >
          Switching to <strong>{subscriptionData.pendingSwitchInterval}</strong> billing on{" "}
          {subscriptionData.currentPeriodEnd
            ? new Intl.DateTimeFormat("en-AU", {
                day: "numeric",
                month: "short",
                year: "numeric",
              }).format(new Date(subscriptionData.currentPeriodEnd))
            : subscriptionData.nextInvoiceDate}
          . No charge today.
        </Typography>
      )}
    </Box>
  );

  // ─── Render: proration dialog ─────────────────────────────────────────────────
  const renderProrationAlert = () => {
    const newIntervalLabel =
      prorationPreview?.newInterval === "year"
        ? "Yearly"
        : prorationPreview?.newInterval === "month"
          ? "Monthly"
          : prorationPreview?.newInterval ?? "";

    return (
      <Dialog
        open={!!prorationPreview}
        onClose={switching ? undefined : cancelSwitchPlan}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Confirm Plan Switch
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <DialogContentText component="div" color="text.primary">
            <Stack spacing={2}>
              <Box
                sx={{
                  bgcolor: "primary.50",
                  border: "1px solid",
                  borderColor: "primary.200",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Switching to
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="primary.main">
                    {newIntervalLabel} Billing
                  </Typography>
                </Stack>
              </Box>

              <Stack spacing={1}>
                {prorationPreview?.effectiveDate && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Effective date
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {prorationPreview.effectiveDate}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Charge today
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    No charge
                  </Typography>
                </Stack>
                {prorationPreview?.nextRenewalAmount && prorationPreview?.nextRenewalDate && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Next invoice
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {prorationPreview.nextRenewalAmount}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={cancelSwitchPlan}
            disabled={switching}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSwitchPlan}
            disabled={switching}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 140 }}
          >
            {switching ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Confirm Switch"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Render: subscribed plan cards ────────────────────────────────────────────
  const renderSubscribedPlans = () => {
    const isCancelled = subscriptionData?.cancelAtPeriodEnd;
    console.log("[PaymentsTab] plan card state:", {
      cancelAtPeriodEnd: subscriptionData?.cancelAtPeriodEnd,
      billingPeriod: subscriptionData?.billingPeriod,
      currentInterval,
      switching,
      pendingPriceId,
    });

    // Returns the correct CardActions button for each plan slot
    const getCardAction = (plan: "free" | "monthly" | "yearly") => {
      if (plan === "free") {
        return (
          <Button
            variant="outlined"
            color="error"
            fullWidth
            size="large"
            onClick={handleManageBilling}
            disabled={subscribing}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
          >
            Downgrade to Free
          </Button>
        );
      }

      const isCurrent =
        (plan === "monthly" && currentInterval === "monthly") ||
        (plan === "yearly" && currentInterval === "yearly");

      // Current plan always shows "Current Plan ✓" — even when cancelled
      if (isCurrent) {
        return (
          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
          >
            Current Plan ✓
          </Button>
        );
      }

      // Cancelled subscription: block plan switching, prompt to resume first
      if (isCancelled) {
        return (
          <Tooltip title="Resume your subscription before switching plans">
            <span style={{ display: "block", width: "100%" }}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                disabled
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
              >
                Switch Plan
              </Button>
            </span>
          </Tooltip>
        );
      }

      // A switch is already scheduled — block a second one until the period renews
      if (subscriptionData?.pendingSwitchInterval) {
        const isPendingTarget =
          (plan === "yearly" && subscriptionData.pendingSwitchInterval === "Yearly") ||
          (plan === "monthly" && subscriptionData.pendingSwitchInterval === "Monthly");
        return (
          <Tooltip
            title={
              isPendingTarget
                ? `Switching to this plan at end of period`
                : `A plan switch is already scheduled`
            }
          >
            <span style={{ display: "block", width: "100%" }}>
              <Button
                variant={isPendingTarget ? "contained" : "outlined"}
                fullWidth
                size="large"
                disabled
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
              >
                {isPendingTarget ? "Switching Soon ✓" : "Switch Plan"}
              </Button>
            </span>
          </Tooltip>
        );
      }

      const targetPriceId =
        plan === "yearly"
          ? perSeatInfo?.yearly?.priceId || price_yearly_id
          : perSeatInfo?.monthly?.priceId || per_seat_price_id;

      const isThisPlanLoading = switching && pendingPriceId === targetPriceId;

      return (
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => handleSwitchPlan(targetPriceId)}
          disabled={switching || pendingPriceId !== null}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            textTransform: "none",
            bgcolor: plan === "yearly" ? "#0284c7" : undefined,
            "&:hover": plan === "yearly" ? { bgcolor: "#0369a1" } : undefined,
          }}
        >
          {isThisPlanLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Switch Plan"
          )}
        </Button>
      );
    };

    return (
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
          <Typography variant="h6" fontWeight="bold">
            Available Plans
          </Typography>
          <Tooltip
            title={
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Billing Rules:
                </Typography>
                <List dense>
                  {billingRules.map((item, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon sx={{ minWidth: 16 }}>
                        <CircleIcon sx={{ fontSize: 8, color: "white" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            }
            arrow
            placement="right"
          >
            <Info style={{ cursor: "pointer" }} />
          </Tooltip>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 3,
            maxWidth: 1200,
          }}
        >
          {/* ── Free ── */}
          <Card
            sx={{
              border: "2px solid #e5e7eb",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              bgcolor: "#fafafa",
            }}
          >
            <CardContent sx={{ p: 4, flex: "1 1 auto" }}>
              <Typography variant="h5" fontWeight="bold" mb={2}>
                Free
              </Typography>
              <Box mb={3}>
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    $0
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    / month
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  No credit card required
                </Typography>
              </Box>
              <List>
                {["Full feature access", "Up to 5 Clients"].map((item, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ px: 4, mt: "auto", py: 4 }}>
              {getCardAction("free")}
            </CardActions>
          </Card>

          {/* ── Monthly ── */}
          <Card
            sx={{
              border:
                currentInterval === "monthly"
                  ? "2px solid #2563eb"
                  : "2px solid #a0a0a0",
              borderRadius: "12px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {currentInterval === "monthly" && (
              <Chip
                label="Current Plan"
                size="small"
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  bgcolor: "#2563eb",
                  color: "white",
                  fontWeight: 700,
                }}
              />
            )}
            <CardContent sx={{ p: 4, flex: "1 1 auto" }}>
              <Typography variant="h5" fontWeight="bold" mb={2}>
                Monthly
              </Typography>
              <Box mb={3}>
                {perSeatLoading ? (
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Loading seats…
                    </Typography>
                  </Box>
                ) : perSeatInfo?.monthly ? (
                  <>
                    <Box
                      display="flex"
                      alignItems="baseline"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Typography
                        variant="h3"
                        fontWeight="bold"
                        color="primary.main"
                      >
                        ${perSeatInfo.monthly.pricePerSeat}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        AUD / user / {perSeatInfo.monthly.billingInterval}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Seats billed = active users (
                      {perSeatInfo.monthly.activeUserCount})
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="primary.dark"
                      mt={1}
                    >
                      Total: ${perSeatInfo.monthly.totalAmount} /{" "}
                      {perSeatInfo.monthly.billingInterval}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="error">
                    Failed to load per-seat pricing.
                  </Typography>
                )}
              </Box>
              <List>
                {features.slice(0, 10).map((item, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ px: 4, mt: "auto", py: 4 }}>
              {getCardAction("monthly")}
            </CardActions>
          </Card>

          {/* ── Yearly ── */}
          <Card
            sx={{
              background:
                currentInterval === "yearly"
                  ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
                  : "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
              border:
                currentInterval === "yearly"
                  ? "2px solid #2563eb"
                  : "2px solid #0284c7",
              borderRadius: "12px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {currentInterval === "yearly" ? (
              <Chip
                label="Current Plan"
                size="small"
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  bgcolor: "#2563eb",
                  color: "white",
                  fontWeight: 700,
                }}
              />
            ) : (
              <Chip
                label={`Save ${percentSaved}%`}
                size="small"
                color="success"
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  fontWeight: 700,
                }}
              />
            )}
            <CardContent sx={{ p: 4, flex: "1 1 auto" }}>
              <Typography variant="h5" fontWeight="bold" mb={2}>
                Yearly
              </Typography>
              <Box mb={3}>
                {perSeatLoading ? (
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Loading seats…
                    </Typography>
                  </Box>
                ) : perSeatInfo?.yearly ? (
                  <>
                    <Box display="flex" alignItems="baseline">
                      <Typography
                        variant="h3"
                        fontWeight="bold"
                        color="primary.main"
                      >
                        ${perSeatInfo.yearly.pricePerSeatMonthly}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" ml={1}>
                        AUD / user / {perSeatInfo.monthly?.billingInterval}{" "}
                        (billed yearly)
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Seats billed = active users (
                      {perSeatInfo.yearly.activeUserCount})
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="primary.dark"
                      mt={1}
                    >
                      Total: ${perSeatInfo.yearly.totalAmount} /{" "}
                      {perSeatInfo.yearly.billingInterval}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="error">
                    Failed to load yearly pricing.
                  </Typography>
                )}
              </Box>
              <Typography variant="subtitle2" mb={1}>
                Save ${savingsPerUser} per user per year billed annually
                upfront.
              </Typography>
              <List>
                {features.map((item, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ px: 4, mt: "auto", py: 4 }}>
              {getCardAction("yearly")}
            </CardActions>
          </Card>
        </Box>
      </Box>
    );
  };

  // ─── Render: recent invoices ──────────────────────────────────────────────────
  const renderRecentInvoices = () => {
    if (
      !subscriptionData?.recentInvoices ||
      subscriptionData.recentInvoices.length === 0
    ) {
      return null;
    }

    return (
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid #e0e0e0",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
            Recent Invoices
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Date</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Amount</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptionData.recentInvoices.map((invoice: any) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{invoice.date}</TableCell>
                    <TableCell>{invoice.amount}</TableCell>
                    <TableCell align="right">
                      {invoice.url && (
                        <Tooltip title="View Invoice">
                          <IconButton
                            size="small"
                            onClick={() => window.open(invoice.url, "_blank")}
                            sx={{ mr: 1 }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {invoice.pdfUrl && (
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(invoice.pdfUrl, "_blank")
                            }
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  // ─── Render: pricing plans (upsell — shown when NOT active) ──────────────────
  const renderPricingPlans = (): JSX.Element => (
    <Box sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Typography variant="h5" fontWeight="bold" mb={4}>
          Choose your plan to activate Prospo for your organisation
        </Typography>
        <Tooltip
          title={
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Billing Rules:
              </Typography>
              <List dense>
                {billingRules.map((item, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemIcon sx={{ minWidth: 16 }}>
                      <CircleIcon sx={{ fontSize: 8, color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          }
          arrow
          placement="right"
        >
          <Info style={{ cursor: "pointer" }} />
        </Tooltip>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
          maxWidth: 1200,
        }}
      >
        {/* Free */}
        <Card
          sx={{
            border: "2px solid #e5e7eb",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: "#fafafa",
          }}
        >
          <CardContent sx={{ p: 4, flex: "1 1 auto" }}>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Free
            </Typography>
            <Box mb={3} minHeight={110}>
              <Box display="flex" alignItems="baseline" gap={1}>
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  $0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / month
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                No credit card required
              </Typography>
            </Box>
            <Box height={10} />
            <List>
              {["Full feature access", "Up to 5 Clients"].map((item, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
          <CardActions sx={{ px: 4, mt: "auto", py: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              disabled
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
                borderColor: "#d1d5db",
                color: "#6b7280",
              }}
            >
              Current Plan
            </Button>
          </CardActions>
        </Card>

        {/* Monthly */}
        <Card
          sx={{
            border: "2px solid #a0a0a0",
            borderRadius: "12px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <CardContent sx={{ p: 4, flex: "1 1 auto" }}>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Monthly
            </Typography>
            <Box mb={3}>
              {perSeatLoading ? (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading seats…
                  </Typography>
                </Box>
              ) : perSeatInfo?.monthly ? (
                <>
                  <Box
                    display="flex"
                    alignItems="baseline"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color="primary.main"
                    >
                      ${perSeatInfo.monthly.pricePerSeat}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AUD / user / {perSeatInfo.monthly.billingInterval}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Seats billed = active users (
                    {perSeatInfo.monthly.activeUserCount})
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="primary.dark"
                    mt={1}
                  >
                    Total: ${perSeatInfo.monthly.totalAmount} /{" "}
                    {perSeatInfo.monthly.billingInterval}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="error">
                  Failed to load per-seat pricing.
                </Typography>
              )}
            </Box>
            <Box height={32} />
            <List>
              {features.slice(0, 10).map((item, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
          <CardActions sx={{ px: 4, mt: "auto", gap: 0.5, py: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleSubscribePerSeat}
              disabled={subscribingId !== null}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#D3D3D3",
                  color: "white",
                  borderColor: "#D3D3D3",
                },
              }}
            >
              {subscribingId ===
              (perSeatInfo?.monthly?.priceId || per_seat_price_id) ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Subscribe"
              )}
            </Button>
          </CardActions>
        </Card>

        {/* Yearly */}
        <Card
          sx={{
            background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
            border: "2px solid #0284c7",
            borderRadius: "12px",
            position: "relative",
          }}
        >
          <Chip
            label={`Save ${percentSaved}%`}
            size="small"
            color="success"
            sx={{ position: "absolute", top: 16, right: 16, fontWeight: 700 }}
          />
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="h5" fontWeight="bold">
                Yearly
              </Typography>
            </Box>
            <Box mb={3}>
              {perSeatLoading ? (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading seats…
                  </Typography>
                </Box>
              ) : perSeatInfo?.yearly ? (
                <>
                  <Box display="flex" alignItems="baseline">
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color="primary.main"
                    >
                      ${perSeatInfo.yearly.pricePerSeatMonthly}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" ml={1}>
                      AUD / user / {perSeatInfo.monthly?.billingInterval}{" "}
                      (billed yearly)
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Seats billed = active users (
                    {perSeatInfo.yearly.activeUserCount})
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="primary.dark"
                    mt={1}
                  >
                    Total: ${perSeatInfo.yearly.totalAmount} /{" "}
                    {perSeatInfo.yearly.billingInterval}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="error">
                  Failed to load yearly pricing.
                </Typography>
              )}
            </Box>
            <Typography variant="subtitle2" mb={1}>
              Save ${savingsPerUser} per user per year billed annually upfront.
            </Typography>
            <List>
              {features.map((item, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
          <CardActions sx={{ px: 4, mt: "auto", gap: 0.5, py: 4 }}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() =>
                handleSubscribe(perSeatInfo?.yearly?.priceId || price_yearly_id)
              }
              disabled={subscribingId !== null}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
                bgcolor: "#0284c7",
              }}
            >
              {subscribingId ===
              (perSeatInfo?.yearly?.priceId || price_yearly_id) ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Subscribe"
              )}
            </Button>
          </CardActions>
        </Card>
      </Box>
    </Box>
  );

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ width: 1600 }}>
      {/* ToS Modal */}
      <TermsOfServiceModal
        open={tosModalOpen}
        onClose={() => {
          setTosModalOpen(false);
          pendingSubscribeRef.current = null;
        }}
        onAccept={handleTosAccept}
        loading={tosLoading}
      />
      {tosError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {tosError}
        </Alert>
      )}

      {isActive ? (
        <Box sx={{ py: 4, width: "100%", maxWidth: 1200 }}>
          {renderStatusBanner()}

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {switchError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setSwitchError(null)}
            >
              {switchError}
            </Alert>
          )}

          {renderProrationAlert()}
          {renderSubscribedPlans()}
          {renderRecentInvoices()}
        </Box>
      ) : (
        <>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {renderPricingPlans()}
        </>
      )}
    </Stack>
  );
};

export default PaymentsTab;
